import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { alpha, useTheme } from '@mui/material/styles';
import Link from 'components/common/Link';
import Modal from 'components/common/Modal';
import { CommonContent, onboardingStepTextSx } from './constants';
import { fetchCompletedTaskIds, type OnboardingTask } from './onboarding';
import type { RenderComponentProps } from './onboarding';

export const LINKEDIN_SOURCING_TASK_ID = 'sourcing_candidate_from_linkedin';

const LINKEDIN_URL = 'https://linkedin.com';
const LINKEDIN_SOURCING_CHECK_INTERVAL_MS = 2000;

let refreshLinkedInSourcingProgress: (() => void) | null = null;

export function registerLinkedInSourcingProgressRefresh(refresh: () => void) {
  refreshLinkedInSourcingProgress = refresh;

  return () => {
    if (refreshLinkedInSourcingProgress === refresh) {
      refreshLinkedInSourcingProgress = null;
    }
  };
}

const pollLinkedInSourcingTaskComplete = async (): Promise<boolean> => {
  const completed = await fetchCompletedTaskIds('harbor101', [LINKEDIN_SOURCING_TASK_ID]);
  return completed.has(LINKEDIN_SOURCING_TASK_ID);
};

const LinkedInSourcingContent = () => {
  const theme = useTheme();
  const [isCheckingSourcing, setIsCheckingSourcing] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!isCheckingSourcing || completed) {
      return;
    }

    let cancelled = false;

    const checkCompletion = async () => {
      const isComplete = await pollLinkedInSourcingTaskComplete();

      if (cancelled || !isComplete) {
        return;
      }

      setCompleted(true);
      setIsCheckingSourcing(false);
      refreshLinkedInSourcingProgress?.();
    };

    void checkCompletion();

    const intervalId = window.setInterval(() => {
      void checkCompletion();
    }, LINKEDIN_SOURCING_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isCheckingSourcing, completed]);

  const handleLinkedInLinkClick = () => {
    setIsCheckingSourcing(true);
    setCompleted(false);
  };

  const showSourcingStatus = isCheckingSourcing || completed;

  return (
    <Box>
      <CommonContent
        alert={
          <>
            For onboarding purposes, your email address will be automatically added to the sourced candidate as their
            primary email address.
          </>
        }
        version="101"
        steps={[
          {
            text: (
              <>
                Go to{' '}
                <Link href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" onClick={handleLinkedInLinkClick}>
                  LinkedIn
                </Link>
              </>
            ),
          },
          {
            text: 'Open your profile page',
          },
          {
            text: (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <p>Click on the Harbor extension icon in your browser toolbar:</p>
                <img src="/guide/101/extension-icon.png" alt="Extension icon" width="30" height="30" />
              </Box>
            ),
          },
          {
            text: 'Log into the Browser Extension using your Harbor credentials',
          },
          {
            text: 'Select Job and click the Add button',
          },
        ]}
      />

      {showSourcingStatus && (
        <Alert
          severity={completed ? 'success' : 'info'}
          icon={
            completed ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 28 }} />
            ) : (
              <CircularProgress size={28} thickness={4} color="inherit" />
            )
          }
          sx={{
            mt: 3,
            py: 2,
            px: 2.5,
            alignItems: completed ? 'center' : 'flex-start',
            border: 2,
            borderColor: completed ? 'success.main' : 'primary.main',
            bgcolor: completed
              ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.1)
              : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.1),
            boxShadow: completed
              ? `0 0 0 1px ${alpha(theme.palette.success.main, 0.25)}`
              : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
            '& .MuiAlert-icon': {
              mr: 1.5,
              mt: completed ? 0 : 0.25,
              p: 0,
              opacity: 1,
            },
            '& .MuiAlert-message': {
              width: '100%',
              py: completed ? 0 : 0.25,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: completed ? 0 : 0.75 }}>
            {!completed && <PersonAddAltIcon sx={{ fontSize: 22, color: 'primary.main' }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
              {completed ? 'Candidate sourced!' : 'Waiting for you to source a candidate'}
            </Typography>
          </Box>
          {!completed && (
            <Typography variant="body2" sx={{ ...onboardingStepTextSx, color: 'text.primary' }}>
              Use the Harbor extension on LinkedIn to add a candidate, then return to this page. We will detect it
              automatically.
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
};

function LinkedInSourcingGuideModal({ onClose }: RenderComponentProps) {
  return (
    <Modal
      open={true}
      title="Source Candidate from LinkedIn"
      renderContent={() => <LinkedInSourcingContent />}
      onClose={onClose}
      hideCloseButton
    />
  );
}

LinkedInSourcingGuideModal.displayName = 'LinkedInSourcingGuideModal';

export const linkedinSourcingOnboardingTask: OnboardingTask = {
  id: LINKEDIN_SOURCING_TASK_ID,
  title: ' Source Candidate from LinkedIn',
  completed: false,
  renderComponent: LinkedInSourcingGuideModal,
};

/** Poll onboarding progress while the LinkedIn sourcing modal is open; close it when the API marks the task complete. */
export function useLinkedInSourcingModalSync(options: {
  dialogOpen: boolean;
  currentTaskId: string | null;
  completedTasks: Set<string>;
  setDialogOpen: (open: boolean) => void;
  loadOnboardingData: () => Promise<void>;
}) {
  const { dialogOpen, currentTaskId, completedTasks, setDialogOpen, loadOnboardingData } = options;
  const isLinkedInSourcingModalOpen = dialogOpen && currentTaskId === LINKEDIN_SOURCING_TASK_ID;

  useEffect(() => registerLinkedInSourcingProgressRefresh(() => void loadOnboardingData()), [loadOnboardingData]);

  useEffect(() => {
    if (!isLinkedInSourcingModalOpen) {
      return;
    }

    if (completedTasks.has(LINKEDIN_SOURCING_TASK_ID)) {
      setDialogOpen(false);
      return;
    }

    void loadOnboardingData();

    const intervalId = window.setInterval(() => {
      void loadOnboardingData();
    }, LINKEDIN_SOURCING_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isLinkedInSourcingModalOpen, completedTasks, setDialogOpen, loadOnboardingData]);
}
